import {
  Button,
  DatePicker,
  Flex,
  Input,
  Select,
  Space,
  Switch,
  TableProps,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AddIcon from "../../../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../../../assets/icons/ViewIcon";
import ConfirmModal from "../../../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../../../components/ui/CustomTable";
import { dbDateFormat } from "../../../../../../constants/constants";
import {
  AllKPIGroupParams,
  KPIGroup,
} from "../../../../../../features/organization/contractual/KPISetup/kpiSetup.type";
import {
  useDeleteKPIGroupMutation,
  useGetAllKPIGroupQuery,
  useUpdateKPIGroupMutation,
} from "../../../../../../features/organization/contractual/KPISetup/kpiSetupApi";
import useError from "../../../../../../hooks/useError";
import useModal from "../../../../../../hooks/useModal";
import useTable from "../../../../../../hooks/useTable";
import { formatDate } from "../../../../../../lib/time";
import { toast } from "../../../../../../lib/toast";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../../../utils/string";
import { addKeyInArray } from "../../../../../../utils/utils";
import CalanderIcon from "../../../../survey/templates/_icons/CalanderIcon";
const { RangePicker } = DatePicker;
const KPIGroups = () => {
  const { open, handleOpen, handleClose } = useModal<KPIGroup>();
  const [updatingRow, setUpdatingRow] = useState<number | null>(null);
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useTable<AllKPIGroupParams>();

  const { data, isLoading, isFetching } = useGetAllKPIGroupQuery({
    params: { ...filter, search: keyword },
  });

  const [updateStatus] = useUpdateKPIGroupMutation();
  const [
    deleteKPIGroup,
    {
      isError: isDeleteError,
      error: deleteError,
      isLoading: deleting,
      isSuccess: deleted,
    },
  ] = useDeleteKPIGroupMutation();

  const handleStatusChange = async (record: KPIGroup, newStatus: string) => {
    setUpdatingRow(record.id);
    try {
      await updateStatus({
        id: record.id,
        data: { status: newStatus },
      }).unwrap();
      toast("success", "KPI group status updated successfully.");
    } catch (err) {
      console.error(err);
      toast("error", "Status update failed");
    } finally {
      setUpdatingRow(null);
    }
  };

  useEffect(() => {
    if (deleted) {
      handleClose();
      toast("success", "KPI group deleted successfully.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  useError(isDeleteError, deleteError, "KPI Group delete failed.");
  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "KPI Group Name",
      dataIndex: "group_name",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Total KPI's",
      align: "center",
      dataIndex: "kpis",
      render: (val) => addCommaInNumber(val?.length),
    },
    {
      title: "Last Updated",
      dataIndex: "updated_at",
      render: (val) => formatDate(val),
    },
    {
      title: "Effective Date",
      dataIndex: "effective_from",
      render: (val) => formatDate(val),
    },
    // {
    //   title: "Used in Slabs",
    //   dataIndex: "used_in_slabs",
    //   render: (val) => addCommaInNumber(val || 0),
    // },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val, record) => (
        <Flex gap={6} align="center" justify="center">
          <Switch
            loading={updatingRow === record.id}
            onChange={() =>
              handleStatusChange(
                record,
                val === "active" ? "inactive" : "active"
              )
            }
            checked={val === "active"}
            className="bg-active"
          />
          <span>{capitalize(val)}</span>
        </Flex>
      ),
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (record) => (
        <Space align="center">
          <Link to={`kpis/${record.id}?title=${record.group_name}`}>
            <Tooltip title="View">
              <Button
                className="action-view"
                size="small"
                icon={<ViewIcon />}
              />
            </Tooltip>
          </Link>
          <Tooltip title="Edit">
            <Link to={`edit/${record.id}`}>
              <Button
                className="action-edit"
                size="small"
                icon={<EditIcon />}
              />
            </Link>
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              onClick={() => handleOpen({ type: "VIEW", data: record })}
              className="action-delete"
              size="small"
              icon={<DeleteIcon />}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <ConfirmModal
        width={450}
        open={open?.open}
        okLoading={deleting}
        okText="Yes, Delete"
        title="Are you sure?"
        handleClose={() => handleClose()}
        handleOk={() => deleteKPIGroup({ id: open.data?.id })}
        subTitle="This action will permanently delete the selected KPI Group. This cannot be undone."
      />

      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data.total}
        dataSource={addKeyInArray(data?.data.groups)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />

            <RangePicker
              style={{ width: 245 }}
              separator="-"
              className="w-full input-bordered"
              format={"DD/MM/YYYY"}
              suffixIcon={<CalanderIcon />}
              placeholder={["dd/mm/yyyy", "dd/mm/yyyy"]}
              onChange={(dates: (Dayjs | null)[] | null) => {
                const [start, end] = dates || [];

                // Call filter only if both dates are selected
                if (start && end) {
                  handleFilter(
                    "effective_from_end",
                    dayjs(start).format(dbDateFormat)
                  );
                  handleFilter(
                    "effective_from_start",
                    dayjs(end).format(dbDateFormat)
                  );
                } else {
                  // Clear filters if user clears dates
                  handleFilter("effective_from_end", undefined);
                  handleFilter("effective_from_start", undefined);
                }
              }}
            />

            <Select
              allowClear
              placeholder="Status"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              onChange={(value) => handleFilter("status", value)}
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
            />

            <Link to={"add"}>
              <Button color="primary" type="primary">
                <AddIcon /> New KPI Batches
              </Button>
            </Link>
          </>
        }
      />
    </>
  );
};

export default KPIGroups;
